import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import UploadIcon from '@mui/icons-material/Publish';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { LoadingButton } from '@/components/shared/LoadingButton';
import type { SortFilterValue } from '@/components/shared/SortFilterDialog';
import { SortFilterControl } from '@/components/shared/SortFilterControl';
import { TableNotification } from '@/components/shared/TableNotification';
import { useUser } from '@/context/UserContext';
import { logger } from '@/services/logging';
import {
  addOrderNote,
  buildSchedulingListRequest,
  downloadSchedule,
  fetchScheduling,
  fetchShipmentStatuses,
  isStatusDisabled,
  NOTE_REQUIRED_STATUSES,
  readDownloadError,
  SCHEDULING_FILTER_FIELDS,
  SCHEDULING_PAGE_SIZE_OPTIONS,
  SCHEDULING_SORT_FIELDS,
  SCHEDULING_TABS,
  submitDeliveryStatus,
  type ScheduleUploadResult,
  type SchedulingTab,
  UPLOAD_SUCCESS_STATUS,
} from '@/services/scheduling/schedulingService';
import type { SchedulingItem } from '@/types';
import { getOrderStatusColor } from '@/utils/orderStatus';
import { ExpandedSchedulingRow } from './ExpandedSchedulingRow';
import {
  ScheduleExportDialog,
  ScheduleImportDialog,
  StatusNoteDialog,
  UploadResultDialog,
} from './ScheduleDialogs';

interface ColumnDefinition {
  id: string;
  label: string;
  tooltip?: string;
  filterKey?: string;
  render: (item: SchedulingItem) => React.ReactNode;
}

interface PendingNoteChange {
  item: SchedulingItem;
  status: string;
}

const HEADER_BG = '#0c479d';
const ROW_HOVER_BG = '#E3F2FC';
const ACTIVE_HEADER_SX = { color: `${HEADER_BG} !important`, fontWeight: '700 !important' };

const OUTLINED_BUTTON_SX = {
  maxHeight: '36px',
  fontSize: '13px',
  letterSpacing: '0.08em',
  color: '#0c479d',
  fontWeight: 700,
  borderColor: 'rgba(0, 0, 0, 0.12)',
};

const HEADER_BUTTON_SX = {
  maxHeight: '36px',
  fontSize: '13px',
  letterSpacing: '0.08em',
  color: '#000',
  backgroundColor: '#fff',
  borderColor: 'rgba(0, 0, 0, .12)',
  fontWeight: 700,
  '&:hover': { borderColor: 'rgba(0, 0, 0, .12)', backgroundColor: '#fff' },
};

const TABLE_SX = {
  fontFamily: 'Roboto, sans-serif',
  backgroundColor: 'white',
  '& .MuiTableHead-root .MuiTableCell-root': {
    fontSize: '14px',
    fontWeight: 500,
    padding: '8px 6px',
    verticalAlign: 'middle',
    color: 'rgba(0, 0, 0, 0.87)',
    backgroundColor: 'white',
  },
  '& .MuiTableBody-root .MuiTableCell-root': {
    fontSize: '14px',
    padding: '7px 6px',
    color: 'rgba(0, 0, 0, 0.87)',
    whiteSpace: 'nowrap',
  },
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('MM/DD/YY') : value;
};

const formatNumber = (value?: number | null) => (
  value == null ? '' : Number(value).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 3 })
);

const stripLeadingZeros = (value?: string | null) => (value ?? '').replace(/^0+(?=.)/, '');

const getRowKey = (item: SchedulingItem) => `${item.orderNumber}|${item.itemNumber ?? ''}|${item.deliveryNumber ?? ''}`;

const Truncated: React.FC<{ value?: string | null; max?: number }> = ({ value, max = 12 }) => {
  const text = value ?? '';
  return text.length > max
    ? <Tooltip title={text}><span>{`${text.slice(0, max)}...`}</span></Tooltip>
    : <span>{text}</span>;
};

const getColumns = (tab: SchedulingTab): ColumnDefinition[] => {
  const ShippingIcon = tab === 'TRUCK' ? LocalShippingIcon : DirectionsBoatIcon;
  const motColumn: ColumnDefinition = tab === 'TRUCK'
    ? { id: 'truckNumber', label: 'Truck Number', filterKey: 'TruckNumber', render: (item) => item.truckNumber }
    : { id: 'vesselNumber', label: 'Vessel Number', filterKey: 'VesselNumber', render: (item) => item.vesselNumber };

  return [
    { id: 'orderNumber', label: 'Order #', filterKey: 'OrderNumber', render: (item) => item.orderNumber },
    { id: 'itemNumber', label: 'Item', filterKey: 'ItemNumber', render: (item) => item.itemNumber },
    { id: 'orderType', label: 'Type', filterKey: 'OrderType', render: (item) => <Truncated value={item.orderType} /> },
    { id: 'plantCode', label: 'Supply Point', filterKey: 'PlantCode', render: (item) => item.plantCode },
    {
      id: 'shipToPlantCode',
      label: 'Ship-To',
      filterKey: 'ShipToPlantCode',
      render: (item) => (
        <Tooltip title={item.shipToPlantCode ?? ''}><span>{stripLeadingZeros(item.shipToPlantCode)}</span></Tooltip>
      ),
    },
    {
      id: 'shippingCondition',
      label: 'Ship Cond.',
      filterKey: 'ShippingCondition',
      render: (item) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ShippingIcon sx={{ fontSize: 20 }} />
          <span>{item.shippingCondition}</span>
        </Box>
      ),
    },
    { id: 'routeCode', label: 'Route', filterKey: 'RouteCode', render: (item) => item.routeCode },
    {
      id: 'materialCode',
      label: 'Material',
      filterKey: 'MaterialCode',
      render: (item) => <Truncated value={stripLeadingZeros(item.materialCode)} />,
    },
    { id: 'package', label: 'Package', filterKey: 'Package', render: (item) => <Truncated value={item.package} /> },
    { id: 'quantity', label: 'Qty', filterKey: 'Quantity', render: (item) => formatNumber(item.quantity) },
    { id: 'unitOfMeasure', label: 'UoM', filterKey: 'UnitOfMeasure', render: (item) => item.unitOfMeasure },
    { id: 'grossWeight', label: 'Gross Weight', filterKey: 'GrossWeight', render: (item) => formatNumber(item.grossWeight) },
    {
      id: 'plannedLoadDate',
      label: 'P. Load Date',
      tooltip: 'Planned Load Date',
      filterKey: 'PlannedLoadDate',
      render: (item) => formatDate(item.plannedLoadDate),
    },
    {
      id: 'requestedDeliveryDate',
      label: 'Req. Delv. Date',
      tooltip: 'Requested Delivery Date',
      filterKey: 'RequestedDeliveryDate',
      render: (item) => formatDate(item.requestedDeliveryDate),
    },
    { id: 'carrier', label: 'Carrier', filterKey: 'Carrier', render: (item) => <Truncated value={item.carrier} /> },
    motColumn,
  ];
};

export const Scheduling: React.FC = () => {
  const { getBusinessUnit, userData } = useUser();
  const businessUnit = getBusinessUnit();

  const [tab, setTab] = useState<SchedulingTab>('TRUCK');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(SCHEDULING_PAGE_SIZE_OPTIONS[0]);
  const [sorts, setSorts] = useState<SortFilterValue[]>([]);
  const [filters, setFilters] = useState<SortFilterValue[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [statusOverrides, setStatusOverrides] = useState<Record<string, string>>({});
  const [updatingKey, setUpdatingKey] = useState<string | null>(null);
  const [pendingNote, setPendingNote] = useState<PendingNoteChange | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [uploadResult, setUploadResult] = useState<ScheduleUploadResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { params, ignoredFilters } = useMemo(
    () => buildSchedulingListRequest({ tab, page: page + 1, pageSize, sorts, filters }),
    [tab, page, pageSize, sorts, filters],
  );

  const { data, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['scheduling', params],
    queryFn: () => fetchScheduling(params),
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  const { data: shipmentStatuses = [] } = useQuery({
    queryKey: ['lookup/shipmentStatuses'],
    queryFn: fetchShipmentStatuses,
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;

  useEffect(() => {
    setExpanded(new Set());
    setStatusOverrides({});
  }, [data]);

  const columns = useMemo(() => getColumns(tab), [tab]);
  const columnCount = columns.length + 2;

  const activeFilterColumns = useMemo(
    () => new Set(filters.map((filter) => filter.property.replace(/(Min|Max)$/, ''))),
    [filters],
  );

  const statusOptions = useMemo(
    () => Array.from(new Set([
      ...shipmentStatuses,
      ...items.map((item) => item.shipmentStatus ?? ''),
      ...Object.values(statusOverrides),
    ].filter(Boolean))),
    [shipmentStatuses, items, statusOverrides],
  );

  const resetToFirstPage = () => {
    setPage(0);
    setExpanded(new Set());
  };

  const handleTabChange = (_: React.SyntheticEvent, nextTab: SchedulingTab) => {
    setTab(nextTab);
    resetToFirstPage();
  };

  const toggleRow = (key: string) => {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  };

  const allExpanded = items.length > 0 && expanded.size === items.length;

  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(items.map(getRowKey)));

  const applyStatusChange = useCallback(async (item: SchedulingItem, status: string, note?: string) => {
    const key = getRowKey(item);
    setErrorMessage(null);
    setUpdatingKey(key);
    setStatusOverrides((previous) => ({ ...previous, [key]: status }));

    try {
      if (note) await addOrderNote(item.orderNumber, note);
      await submitDeliveryStatus(item, status, businessUnit);
    } catch (changeError) {
      setStatusOverrides((previous) => {
        const next = { ...previous };
        delete next[key];
        return next;
      });
      setErrorMessage('Failed to update the shipment status. Please try again.');
      logger.error('Failed to update shipment status', changeError as Error);
    } finally {
      setUpdatingKey(null);
    }
  }, [businessUnit]);

  const handleStatusChange = (item: SchedulingItem, status: string) => {
    if (NOTE_REQUIRED_STATUSES.includes(status)) {
      setPendingNote({ item, status });
      return;
    }

    void applyStatusChange(item, status);
  };

  const handleNoteSubmit = async (note: string) => {
    if (!pendingNote) return;

    await applyStatusChange(pendingNote.item, pendingNote.status, note);
    setPendingNote(null);
  };

  const handleExport = async (format: string) => {
    setExportOpen(false);
    setIsExtracting(true);
    setErrorMessage(null);

    try {
      const { blob, filename } = await downloadSchedule({
        tab,
        format,
        source: businessUnit,
        userId: userData.userId,
        filters,
      });
      saveAs(blob, filename);
      // Exports advance IM-Confirmed deliveries, so the table is stale afterwards.
      void refetch();
    } catch (exportError) {
      logger.error('Schedule export failed', exportError as Error);
      setErrorMessage(await readDownloadError(exportError));
    } finally {
      setIsExtracting(false);
    }
  };

  const handleImportComplete = (result: ScheduleUploadResult) => {
    setImportOpen(false);
    setUploadResult(result);
  };

  const handleUploadResultClose = () => {
    if (uploadResult?.status === UPLOAD_SUCCESS_STATUS) void refetch();
    setUploadResult(null);
  };

  const notificationType = isError
    ? ((error as { code?: string })?.code === 'ECONNABORTED' ? 'Timeout' : 'Error')
    : isFetching
      ? 'Loading'
      : total === 0
        ? (filters.length > 0 ? 'Filter' : 'Empty')
        : null;

  return (
    <Box sx={{ p: 0, m: 0, minHeight: 'calc(100vh - 64px)', backgroundColor: '#f2f4f6' }}>
      <Toolbar
        disableGutters
        sx={{
          minHeight: '64px',
          width: '100%',
          px: '16px',
          backgroundColor: `${HEADER_BG} !important`,
          color: '#fff !important',
          alignItems: 'center',
        }}
      >
        <Typography sx={{ color: '#fff', fontSize: '28px', fontWeight: 400, lineHeight: '36px' }}>
          Scheduling
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <LoadingButton
          id="qa-importManifest-btn"
          variant="outlined"
          startIcon={<UploadIcon />}
          loading={importOpen}
          loadingText="IMPORTING"
          onClick={() => setImportOpen(true)}
          sx={HEADER_BUTTON_SX}
        >
          {tab === 'TRUCK' ? 'IMPORT CARRIER MANIFEST' : 'IMPORT VESSEL BOOKING'}
        </LoadingButton>
      </Toolbar>

        <Tabs value={tab} onChange={handleTabChange} sx={{ backgroundColor: '#fff', px: 2 }}>
          {SCHEDULING_TABS.map((item) => (
            <Tab key={item} id={`qa-${item.toLowerCase()}-lnk`} value={item} label={item} />
          ))}
        </Tabs>

      <Box sx={{ m: '20px' }}>
        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mx: 2, mt: 2 }}>{errorMessage}</Alert>
        )}
        {ignoredFilters.length > 0 && (
          <Alert severity="warning" sx={{ mx: 2, mt: 2 }}>
            {`Not applied: ${ignoredFilters.map((filter) => filter.label).join(', ')}. `
              + 'Only one Min/Max bound per date column is supported, and dates must be valid.'}
          </Alert>
        )}

        <Toolbar
          disableGutters
          sx={{ px: 0, py: 0, mb: '20px', minHeight: '48px !important', gap: 1.5, flexWrap: 'wrap', justifyContent: 'space-between' }}
        >
          <Button
            id="qa-refreshTable-btn"
            variant="outlined"
            onClick={() => void refetch()}
            disabled={isFetching}
            sx={OUTLINED_BUTTON_SX}
          >
            REFRESH TABLE
          </Button>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button
              id={allExpanded ? 'qa-collapseAll-btn' : 'qa-expandAll-btn'}
              variant="outlined"
              startIcon={allExpanded ? <ChevronRightIcon /> : <ExpandMoreIcon />}
              onClick={toggleAll}
              disabled={items.length === 0}
              sx={OUTLINED_BUTTON_SX}
            >
              {allExpanded ? 'COLLAPSE ALL' : 'EXPAND ALL'}
            </Button>
            <SortFilterControl
              mode="sort"
              fields={SCHEDULING_SORT_FIELDS}
              activeFields={sorts}
              onApply={(next) => { setSorts(next); resetToFirstPage(); }}
              buttonId="qa-table-sort-btn"
              buttonSx={OUTLINED_BUTTON_SX}
            />
            <SortFilterControl
              mode="filter"
              fields={SCHEDULING_FILTER_FIELDS}
              activeFields={filters}
              onApply={(next) => { setFilters(next); resetToFirstPage(); }}
              buttonId="qa-table-filter-btn"
              buttonSx={OUTLINED_BUTTON_SX}
            />
            <LoadingButton
              id="qa-exportSchedule-btn"
              variant="contained"
              disableElevation
              startIcon={<DownloadIcon />}
              loading={isExtracting}
              loadingText="EXPORTING"
              onClick={() => setExportOpen(true)}
              sx={{
                maxHeight: '36px',
                fontSize: '13px',
                letterSpacing: '0.08em',
                color: 'common.white',
                backgroundColor: `${HEADER_BG} !important`,
              }}
            >
              EXPORT SCHEDULE
            </LoadingButton>
          </Box>
        </Toolbar>

        <Paper square elevation={0} sx={{ width: '100%', overflow: 'hidden', backgroundColor: '#fff' }}>
        {notificationType && (
          <TableNotification type={notificationType} onTryAgainEvent={() => void refetch()} />
        )}

        {!notificationType && (
          <>
            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
              <Table size="small" stickyHeader sx={TABLE_SX}>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        id={`qa-scheduling-${column.id}-hdr`}
                        sx={activeFilterColumns.has(column.filterKey ?? '') ? ACTIVE_HEADER_SX : undefined}
                      >
                        {column.tooltip ? <Tooltip title={column.tooltip}><span>{column.label}</span></Tooltip> : column.label}
                      </TableCell>
                    ))}
                    <TableCell
                      id="qa-scheduling-shipmentStatus-hdr"
                      sx={activeFilterColumns.has('ShipmentStatus') ? ACTIVE_HEADER_SX : undefined}
                    >
                      Shipment Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => {
                    const key = getRowKey(item);
                    const isExpanded = expanded.has(key);
                    const status = statusOverrides[key] ?? item.shipmentStatus ?? '';
                    const isUpdating = updatingKey === key;

                    return (
                      <React.Fragment key={`${key}-${index}`}>
                        <TableRow
                          hover
                          onClick={() => toggleRow(key)}
                          sx={{
                            cursor: 'pointer',
                            opacity: isUpdating ? 0.55 : 1,
                            '&:hover': { backgroundColor: ROW_HOVER_BG },
                          }}
                        >
                          <TableCell id="qa-scheduling-expand">
                            {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                          </TableCell>
                          {columns.map((column) => (
                            <TableCell key={column.id}>{column.render(item)}</TableCell>
                          ))}
                          <TableCell id="qa-scheduling-shipmentStatus" sx={{ minWidth: 190 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <FiberManualRecordIcon sx={{ fontSize: 12, color: getOrderStatusColor(status) }} />
                              <Select
                                id={`qa-shipmentStatus-sel-${index}`}
                                value={status}
                                variant="standard"
                                displayEmpty
                                disableUnderline
                                disabled={isUpdating}
                                renderValue={(value) => String(value || '*UNMAPPED*')}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) => handleStatusChange(item, String(event.target.value))}
                                sx={{ fontSize: '14px', flex: 1, minWidth: 0 }}
                              >
                                {statusOptions.map((option) => (
                                  <MenuItem key={option} value={option} disabled={isStatusDisabled(option)} sx={{ fontSize: '14px' }}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                            </Box>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow>
                            <TableCell id="qa-scheduling-expandedRow" colSpan={columnCount} sx={{ p: '0 !important' }}>
                              <ExpandedSchedulingRow item={item} />
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              id="qa-paginator"
              component="div"
              count={total}
              page={page}
              labelRowsPerPage="Items per page:"
              rowsPerPage={pageSize}
              rowsPerPageOptions={SCHEDULING_PAGE_SIZE_OPTIONS}
              showFirstButton
              showLastButton
              onPageChange={(_, nextPage) => { setPage(nextPage); setExpanded(new Set()); }}
              onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); resetToFirstPage(); }}
            />
          </>
        )}
        </Paper>
      </Box>

      <ScheduleImportDialog
        open={importOpen}
        tab={tab}
        source={businessUnit}
        onClose={() => setImportOpen(false)}
        onComplete={handleImportComplete}
      />
      <ScheduleExportDialog
        open={exportOpen}
        tab={tab}
        onClose={() => setExportOpen(false)}
        onDownload={(format) => void handleExport(format)}
      />
      <UploadResultDialog
        result={uploadResult}
        operation={`Bulk ${tab.toLowerCase()}`}
        type={tab.toLowerCase()}
        onClose={handleUploadResultClose}
      />
      <StatusNoteDialog
        open={!!pendingNote}
        orderNumber={pendingNote?.item.orderNumber ?? ''}
        status={pendingNote?.status ?? ''}
        isSubmitting={!!pendingNote && updatingKey === getRowKey(pendingNote.item)}
        onCancel={() => setPendingNote(null)}
        onSubmit={(note) => void handleNoteSubmit(note)}
      />
    </Box>
  );
};
